import { Box, styled } from '@mui/material';
import { ConstructionIcon } from 'lucide-react';
import { type JSX } from 'react';
import { MAINTENANCE_DEFAULT_DESCRIPTION } from '@/constants/Maintenance/maintenance';
import maintenanceBackground from '@/images/MaintenanceBackground.svg';
import { corePalette } from '@/themes/colours';
import { formatMaintenanceTime } from '@/utils/functions/maintenance';
import ZeroDashMark from '@/components/CustomIcons/ZeroDashMark';
import CustomTypography from '../Common/Typography';

const Page = styled('main')({
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  width: '100%',
  padding: '48px 24px',
  overflow: 'hidden',
  backgroundColor: corePalette.grey10,
});

const Backdrop = styled(Box)({
  position: 'absolute',
  inset: 0,
  backgroundImage: `url(${maintenanceBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  pointerEvents: 'none',
});

const ContentColumn = styled(Box)({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
});

const LogoRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '18px',
});

const WindowCard = styled(Box)({
  display: 'inline-flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: '10px',
  marginTop: '34px',
  padding: '15px 25px',
  borderRadius: '12px',
  border: `1px solid ${corePalette.grey50}`,
  backgroundColor: corePalette.white,
  color: corePalette.orange150,
});

interface IProps {
  startsAt: string;
  expectedEndAt: string;
  description?: string;
}

export default function MaintenancePage({
  startsAt,
  expectedEndAt,
  description = MAINTENANCE_DEFAULT_DESCRIPTION,
}: IProps): JSX.Element {
  return (
    <Page>
      <Backdrop aria-hidden="true" />

      <ContentColumn>
        <LogoRow>
          <ZeroDashMark />
          <CustomTypography
            sx={{
              color: corePalette.offBlack100,
              fontWeight: 700,
              fontSize: '40px',
              lineHeight: '48px',
              letterSpacing: '-0.015em',
            }}
          >
            ZeroDash
          </CustomTypography>
        </LogoRow>

        <CustomTypography
          component="h1"
          sx={{
            marginTop: '27px',
            color: corePalette.offBlack100,
            fontWeight: 500,
            fontSize: '48px',
            lineHeight: '1.2em',
          }}
        >
          ZeroDash is mutating
        </CustomTypography>

        <CustomTypography
          variant="bodyRegular"
          sx={{ marginTop: '19px', maxWidth: '680px', color: corePalette.grey200 }}
        >
          {description}
        </CustomTypography>

        <WindowCard>
          <ConstructionIcon size={17} />
          <CustomTypography variant="bodyRegular" sx={{ color: corePalette.grey200 }}>
            Scheduled window:
          </CustomTypography>
          <CustomTypography
            variant="bodyRegular"
            sx={{ color: corePalette.orange200, fontWeight: 500 }}
          >
            {`${formatMaintenanceTime(startsAt)} – ${formatMaintenanceTime(expectedEndAt)}`}
          </CustomTypography>
        </WindowCard>

        <CustomTypography
          variant="bodySmall"
          sx={{ marginTop: '32px', color: corePalette.grey100 }}
        >
          If this takes longer than expected, please contact your ZERO platform administrator.
        </CustomTypography>
      </ContentColumn>
    </Page>
  );
}
